import React, { useEffect, useRef, useState, useMemo } from "react";
import { toast } from "sonner";
import { useReactToPrint } from "react-to-print";
import { Printer, X } from "lucide-react";
import { api, peso, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

export default function ZReportDialog({ open, onClose, mode = "z", shift = null, onShiftClosed = null }) {
  // mode: "z" = Close Shift (with cash reconciliation, end-of-day flavor)
  //       "x" = Mid-shift snapshot (read-only, no cash drawer section)
  const isZ = mode === "z";
  const title = isZ ? "Close Shift · Z-Report" : "Mid-Shift Snapshot · X-Report";
  const subtitle = isZ
    ? "End-of-day summary. Counts only POS sales (excludes online orders)."
    : "Live snapshot of today's POS activity. Read-only — does not close the shift.";
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [report, setReport] = useState(null);
  // Pre-fill opening from the cashier's open shift when present; otherwise blank
  // so managers/admins can type it manually.
  const [openingCash, setOpeningCash] = useState(() => (shift ? String(shift.opening_cash) : ""));
  const [countedCash, setCountedCash] = useState("");
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);

  // react-to-print: prints only the Z-report block via a sandboxed iframe.
  // The @page rule is built per-print from the live node's measured height so
  // the printed paper is exactly receipt-length (no blank tail).
  // Thermal-safe layout (matches AdminPOS.jsx): <pre> monospace block with
  // space-padded alignment, ASCII-only glyphs, 12pt bold weight.
  const zPrintRef = useRef(null);
  const PX_TO_MM = 0.264583;
  const ZCOLS = 32;
  const zCenter = (s) => {
    const t = String(s ?? "");
    if (t.length >= ZCOLS) return t.slice(0, ZCOLS);
    return " ".repeat(Math.floor((ZCOLS - t.length) / 2)) + t;
  };
  const zRule = "-".repeat(ZCOLS);
  const zRow = (left, right) => {
    const l = String(left ?? "");
    const r = String(right ?? "");
    const maxL = Math.max(1, ZCOLS - r.length - 1);
    if (l.length <= maxL) return l + " ".repeat(ZCOLS - l.length - r.length) + r;
    const first = l.slice(0, maxL);
    const rest = l.slice(maxL);
    const firstLine = first + " ".repeat(ZCOLS - first.length - r.length) + r;
    const cont = [];
    let i = 0;
    while (i < rest.length) { cont.push("  " + rest.slice(i, i + (ZCOLS - 2))); i += (ZCOLS - 2); }
    return [firstLine, ...cont].join("\n");
  };
  const zPesoTxt = (n) => {
    const v = Number(n || 0);
    return `P${v.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const RECEIPT_CSS = `
    html, body { margin: 0; padding: 0; background: #fff; }
    .pos-print-receipt {
      display: block !important;
      position: static !important;
      visibility: visible !important;
      left: auto !important;
      width: 80mm;
      padding: 2mm 2mm 4mm 2mm;
      font-family: 'Courier New', Courier, monospace;
      font-size: 12pt;
      font-weight: 700;
      line-height: 1.25;
      color: #000;
      box-sizing: border-box;
    }
    .pos-print-receipt .pr-h1 {
      font-size: 18pt;
      font-weight: 900;
      text-align: center;
      line-height: 1.1;
      margin: 0 0 1mm 0;
      letter-spacing: 0;
    }
    .pos-print-receipt .pr-h2 {
      font-size: 13pt;
      font-weight: 900;
      text-align: center;
      margin: 0;
    }
    .pos-print-receipt pre.pr-body {
      margin: 0;
      padding: 0;
      font-family: inherit;
      font-size: inherit;
      font-weight: inherit;
      line-height: inherit;
      white-space: pre;
      color: #000;
    }
  `;
  // Build the printable Z/X-Report text body. Pure ASCII so the bitmap that
  // RawBT sends to the Officom OC-T80A has no missing-glyph noise.
  const buildZReportText = () => {
    if (!report) return "";
    const lines = [];
    lines.push(zCenter("Felix Taghap Street, Pob 2"));
    lines.push(zCenter("Oroquieta City 7207"));
    lines.push(zCenter(isZ ? "** Z-REPORT **" : "** X-REPORT (snapshot) **"));
    lines.push(zCenter(report.date));
    const cashiers = (report.cashier_names || []).join(", ") || "-";
    lines.push(zCenter("Cashier: " + cashiers));
    if (isZ && shift) {
      lines.push(zCenter("Opened: " + new Date(shift.opened_at).toLocaleString("en-PH")));
    }
    lines.push(zRule);
    lines.push(zRow("TOTAL SALES", zPesoTxt(report.totals.sales)));
    lines.push(zRow("Transactions", String(report.totals.transactions)));
    lines.push(zRow("Avg ticket", zPesoTxt(report.totals.average)));
    lines.push(zRule);
    lines.push(zCenter("PAYMENT METHODS"));
    (report.payments || []).forEach((p) => {
      const label = (p.method.charAt(0).toUpperCase() + p.method.slice(1)) + ` (x${p.count})`;
      lines.push(zRow(label, zPesoTxt(p.total)));
    });
    if (report.top_items && report.top_items.length > 0) {
      lines.push(zRule);
      lines.push(zCenter("TOP PRODUCTS"));
      report.top_items.forEach((it) => {
        lines.push(zRow(`${it.qty}x ${it.name}`, zPesoTxt(it.total)));
      });
    }
    if (countedCash !== "") {
      lines.push(zRule);
      lines.push(zCenter("CASH DRAWER"));
      lines.push(zRow("Opening", zPesoTxt(Number(openingCash) || 0)));
      lines.push(zRow("Cash sales", zPesoTxt(cashSalesTotal)));
      lines.push(zRow("Expected", zPesoTxt(expectedCash)));
      lines.push(zRow("Counted", zPesoTxt(Number(countedCash) || 0)));
      lines.push(zRow("Variance", (variance > 0 ? "+" : "") + zPesoTxt(variance || 0)));
    }
    lines.push(zRule);
    lines.push(zCenter("End of report"));
    lines.push(zCenter("arbeminimart.com"));
    return lines.join("\n");
  };

  const handlePrint = useReactToPrint({
    contentRef: zPrintRef,
    documentTitle: `Arbe-${isZ ? "Z-Report" : "X-Report"}-${date}`,
    pageStyle: () => {
      const hPx = zPrintRef.current ? zPrintRef.current.scrollHeight : 0;
      const hMm = Math.max(40, Math.ceil(hPx * PX_TO_MM) + 4);
      return `@page { size: 80mm ${hMm}mm; margin: 0; }\n${RECEIPT_CSS}`;
    },
  });

  const handleCloseShift = async () => {
    if (countedCash === "") { toast.error("Count the drawer cash first"); return; }
    const n = Number(countedCash);
    if (Number.isNaN(n) || n < 0) { toast.error("Counted cash must be 0 or higher"); return; }
    setClosing(true);
    try {
      const { data } = await api.post("/pos/shift/close", { counted_cash: n });
      // Print the Z-Report right after closing so the cashier walks away with a paper copy.
      setTimeout(() => { try { handlePrint(); } catch { /* user cancelled */ } }, 250);
      if (onShiftClosed) onShiftClosed(data);
      setTimeout(onClose, 800);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setClosing(false);
    }
  };

  // Cash drawer reconciliation: expected ending = opening + cash sales
  const cashSalesTotal = useMemo(() => {
    if (!report) return 0;
    const cash = report.payments.find((p) => p.method === "cash");
    return cash ? cash.total : 0;
  }, [report]);
  const expectedCash = (Number(openingCash) || 0) + cashSalesTotal;
  const variance = countedCash !== "" ? (Number(countedCash) || 0) - expectedCash : null;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/pos/z-report?date=${date}`);
      setReport(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, date]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{title}</DialogTitle>
          <p className="text-xs text-[#738380]">{subtitle}</p>
        </DialogHeader>

        <div className="flex items-center gap-2 py-2 border-y border-[#E1E5DF]">
          <Label htmlFor="z-date" className="text-xs">Date:</Label>
          <Input
            id="z-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-9 max-w-[160px] text-sm"
            data-testid="z-date-picker"
          />
        </div>

        <div className="overflow-y-auto flex-1 -mx-2 px-2 py-2 text-sm">
          {loading && <div className="text-center text-[#738380] py-10">Loading…</div>}
          {!loading && report && (
            <>
              <div className="bg-[#F1F3EE] rounded-lg p-3 mb-3" data-testid="z-totals">
                <div className="flex justify-between"><span>Total Sales</span><span className="font-heading font-extrabold text-xl text-[#2F5233]">{peso(report.totals.sales)}</span></div>
                <div className="flex justify-between text-xs text-[#4A5D5A]"><span>Transactions</span><span>{report.totals.transactions}</span></div>
                <div className="flex justify-between text-xs text-[#4A5D5A]"><span>Avg ticket</span><span>{peso(report.totals.average)}</span></div>
                <div className="flex justify-between text-xs text-[#4A5D5A]"><span>Cashier(s)</span><span className="truncate ml-2">{report.cashier_names.join(", ") || "—"}</span></div>
              </div>

              <div className="mb-3">
                <div className="overline mb-1">By Payment Method</div>
                {report.payments.length === 0 && <div className="text-xs text-[#738380]">No sales today.</div>}
                {report.payments.map((p) => (
                  <div key={p.method} className="flex justify-between text-sm py-0.5">
                    <span className="capitalize">{p.method} <span className="text-[10px] text-[#738380]">×{p.count}</span></span>
                    <span className="font-semibold">{peso(p.total)}</span>
                  </div>
                ))}
              </div>

              {report.top_items.length > 0 && (
                <div className="mb-3">
                  <div className="overline mb-1">Top Products</div>
                  {report.top_items.map((it) => (
                    <div key={it.name} className="flex justify-between text-xs py-0.5">
                      <span className="truncate mr-2">{it.qty}× {it.name}</span>
                      <span className="font-semibold">{peso(it.total)}</span>
                    </div>
                  ))}
                </div>
              )}

              {isZ && (
              <div className="border-t border-[#E1E5DF] pt-3">
                <div className="overline mb-2">Cash Drawer Reconciliation</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">
                      Opening cash {shift && <span className="text-[10px] text-[#2F5233] font-semibold">· from shift</span>}
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={openingCash}
                      onChange={(e) => setOpeningCash(e.target.value)}
                      readOnly={!!shift}
                      className={`h-9 text-sm ${shift ? "bg-[#F1F3EE]" : ""}`}
                      data-testid="z-opening"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Counted ending</Label>
                    <Input type="number" step="0.01" value={countedCash} onChange={(e) => setCountedCash(e.target.value)} className="h-9 text-sm" placeholder="Count drawer cash" data-testid="z-counted" />
                  </div>
                </div>
                <div className="mt-2 text-xs space-y-0.5">
                  <div className="flex justify-between text-[#4A5D5A]"><span>+ Cash sales</span><span>{peso(cashSalesTotal)}</span></div>
                  <div className="flex justify-between font-semibold"><span>= Expected ending cash</span><span>{peso(expectedCash)}</span></div>
                  {variance !== null && (
                    <div className={`flex justify-between font-bold pt-1 border-t border-[#E1E5DF] ${variance === 0 ? "text-[#2F5233]" : "text-[#C62828]"}`} data-testid="z-variance">
                      <span>Variance</span>
                      <span>{variance > 0 ? "+" : ""}{peso(variance)}</span>
                    </div>
                  )}
                </div>
              </div>
              )}
            </>
          )}
        </div>

        <div className="pt-3 border-t border-[#E1E5DF] space-y-2">
          {isZ && shift && (
            <Button
              onClick={handleCloseShift}
              disabled={closing || countedCash === ""}
              className="w-full h-11 bg-[#C62828] hover:bg-[#8E1F1F] text-white font-bold"
              data-testid="z-close-shift-confirm"
            >
              {closing ? "Closing shift…" : "Close shift & print Z-Report"}
            </Button>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={onClose} data-testid="z-close">
              <X className="h-4 w-4 mr-1" /> {isZ && shift ? "Cancel" : "Close"}
            </Button>
            <Button
              onClick={handlePrint}
              disabled={!report || report.totals.transactions === 0}
              className="bg-[#1C2826] hover:bg-black text-white"
              data-testid="z-print"
            >
              <Printer className="h-4 w-4 mr-1" /> {isZ ? "Print Z-Report" : "Print X-Report"}
            </Button>
          </div>
        </div>

        {/* Printable Z-Report (80mm thermal). Thermal-safe: <pre> ASCII text. */}
        {report && (
          <div ref={zPrintRef} id="pos-z-print-area" className="pos-print-receipt">
            <div className="pr-h1">ARBE MINIMART</div>
            <pre className="pr-body">{buildZReportText()}</pre>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
